import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../lib/api.js";
import {
  configInterviewLanguages,
  languageBadge,
  normaliseLanguages,
  pickerRows,
  sameLanguages,
  type OfferedLanguage,
} from "./interviewLanguages.js";
import { useInterviewLanguages } from "./useInterviewLanguages.js";

/**
 * Which languages a candidate may be interviewed in (WS4, Sep 2026).
 *
 * Two homes, one control — the same split as the weights:
 *   - `LanguagePicker` — the controlled checkbox-pill field inside the New Request wizard. The
 *     wizard owns the selection and sends it as `config.interviewLanguages`.
 *   - `RequisitionLanguagesPanel` — the same control on the requisition page, reading and
 *     writing GET/PATCH /requisitions/{id}/languages.
 *
 * English is ticked and locked everywhere: the consent notice is English-anchored (a translation
 * is shown BESIDE it, never instead), so there is no such thing as a requisition that does not
 * offer English. A change here reaches candidates who have not yet consented; anyone who already
 * chose a language keeps it — the choice lives on their candidacy, not on the request.
 */

export function LanguagePicker({
  offered,
  value,
  onChange,
  disabled,
  degraded,
  loading,
}: {
  offered: readonly OfferedLanguage[];
  value: readonly string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  /** The offered list is a fallback, not the API's answer — say so instead of hiding it. */
  degraded?: boolean;
  loading?: boolean;
}): ReactNode {
  const rows = pickerRows(offered, value);
  const selected = normaliseLanguages(value);
  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }} role="group" aria-label="Interview languages">
        {rows.map((r) => {
          const on = (selected as readonly string[]).includes(r.code);
          const locked = r.code === "en";
          return (
            <label
              key={r.code}
              title={
                locked
                  ? "English is always offered — the consent notice and the report are anchored to it."
                  : r.offered
                    ? `${r.label} — ${r.nativeLabel}`
                    : `${r.label} is on this request but is no longer offered by your organisation's settings; untick it to stop offering it to new candidates.`
              }
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                padding: "6px 11px",
                borderRadius: 20,
                border: `1px solid ${on ? "var(--teal)" : "var(--line)"}`,
                background: on ? "var(--teal-wash)" : "var(--surface)",
                color: on ? "var(--teal-ink)" : "var(--ink)",
                fontSize: 12.5,
                fontWeight: 600,
                cursor: disabled || locked ? "default" : "pointer",
                opacity: disabled && !on ? 0.6 : 1,
              }}
            >
              <input
                type="checkbox"
                checked={on}
                disabled={disabled || locked}
                onChange={() => {
                  if (locked) return;
                  onChange(on ? selected.filter((c) => c !== r.code) : [...selected, r.code]);
                }}
                style={{ margin: 0 }}
              />
              <span>{r.label}</span>
              {r.nativeLabel !== r.label && (
                <span className="mut" style={{ fontWeight: 500 }}>
                  {r.nativeLabel}
                </span>
              )}
              {!r.offered && !locked && (
                <span className="pill r" style={{ fontSize: 12 }}>
                  no longer offered
                </span>
              )}
            </label>
          );
        })}
      </div>
      <p className="mut sm" style={{ margin: "8px 0 0" }}>
        {loading
          ? "Checking which languages your organisation can offer…"
          : degraded
            ? "Only English is available right now — the language list could not be loaded, so nothing else can be offered from here until it is."
            : rows.length <= 1
              ? "Only English is available — an owner enables other languages in the admin console once their consent text has been reviewed."
              : "The candidate picks ONE of these at consent. Asha speaks and listens in it; the questions and scoring stay comparable across languages, and the report shows the original answer with an English rendering beneath it."}
      </p>
    </div>
  );
}

/** GET /requisitions/{id}/languages — the current set plus what the org may offer today. Every
 *  field optional so a payload from an older API (or one that spells the keys differently)
 *  degrades to the stored config and the org-wide list instead of a blank editor. */
interface LanguagesResponse {
  interviewLanguages?: string[];
  /** stored ∩ live — what a candidate on this requisition is offered today. */
  offered?: OfferedLanguage[];
  /** every live language the org may add — what the picker is built from. */
  available?: OfferedLanguage[];
}

export function RequisitionLanguagesPanel({
  requisitionId,
  initial,
  canEdit,
  onSaved,
}: {
  requisitionId: string;
  /** What the requisition's stored config says — the fallback when GET has no `interviewLanguages`. */
  initial: readonly string[];
  canEdit: boolean;
  onSaved?: () => void;
}): ReactNode {
  const queryClient = useQueryClient();
  const current = useQuery({
    queryKey: ["languages", requisitionId],
    queryFn: () => api.get<LanguagesResponse>(`/requisitions/${requisitionId}/languages`),
    retry: 1,
  });
  // The org-wide list is the fallback for a GET that carries no `offered` (older API) or failed
  // outright — the editor must still let a recruiter see and save what the config already says.
  const orgWide = useInterviewLanguages(current.isError || (current.isSuccess && !current.data?.available));
  const saved = normaliseLanguages(current.data?.interviewLanguages ?? initial);
  // The picker is built from what the org may ADD (`available`), not from what this requisition
  // already offers — otherwise a live language could never be added to an existing requisition.
  const offered = current.data?.available ?? current.data?.offered ?? orgWide.languages;

  const [draft, setDraft] = useState<string[] | undefined>(undefined);
  useEffect(() => {
    if ((current.isSuccess || current.isError) && draft === undefined) setDraft(saved);
    // `saved` is derived from the query result; re-seeding on every render would clobber typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.isSuccess, current.isError, draft]);
  const [msg, setMsg] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (next: string[]) =>
      api.patch<{ changed?: boolean }>(`/requisitions/${requisitionId}/languages`, {
        // Never an empty list, never absent: "English only" on PATCH is the explicit ["en"] — the
        // schema's min(1) is what stops a client from writing "no language" by accident.
        interviewLanguages: configInterviewLanguages(next) ?? ["en"],
      }),
    onSuccess: (res, next) => {
      setMsg(res?.changed === false ? "No change." : "Saved — new invites offer these languages.");
      // Settle the draft on what was just written rather than waiting on the refetch: until the
      // query comes back, `saved` still holds the old set and Save would look dirty again.
      setDraft(normaliseLanguages(next));
      void queryClient.invalidateQueries({ queryKey: ["languages", requisitionId] });
      void queryClient.invalidateQueries({ queryKey: ["funnel", requisitionId] });
      void queryClient.invalidateQueries({ queryKey: ["requisitions"] });
      onSaved?.();
    },
    onError: (err: unknown) => {
      // A 400 names the language the org may not offer (the owner turned it off since) — that
      // message is the useful one; anything else gets the generic line.
      const body = err instanceof ApiError ? (err.body as { message?: string } | undefined) : undefined;
      setMsg(body?.message ?? (err instanceof Error ? err.message : "Couldn't save the languages."));
    },
  });

  if (current.isPending || draft === undefined) return <p className="mut sm">Loading languages…</p>;
  const dirty = !sameLanguages(draft, saved);

  return (
    <div className="card pad" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontWeight: 600, fontSize: 13 }}>Languages the candidate may interview in</div>
      <LanguagePicker
        offered={offered}
        value={draft}
        onChange={(n) => {
          setDraft(n);
          setMsg(null);
        }}
        disabled={!canEdit || save.isPending}
        degraded={current.isError && orgWide.degraded}
        loading={orgWide.isLoading}
      />
      {current.isError && (
        <p className="mut sm" style={{ margin: 0 }}>
          Couldn't read this request's language settings from the API — showing what its stored
          configuration says.
        </p>
      )}
      <div className="banner info" style={{ fontSize: 12.5 }}>
        <span>
          Applies to candidates who have not yet consented. Anyone who already chose a language keeps
          it — the choice is recorded on their consent, not on this request.
        </span>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button
          type="button"
          className="btn sm primary"
          disabled={!canEdit || !dirty || save.isPending}
          onClick={() => save.mutate(draft)}
        >
          {save.isPending ? "Saving…" : "Save languages"}
        </button>
        <button
          type="button"
          className="btn sm ghost"
          disabled={!dirty || save.isPending}
          onClick={() => {
            setDraft(saved);
            setMsg(null);
          }}
        >
          Discard changes
        </button>
        {msg && (
          <span className="mut sm" role="status" aria-live="polite">
            {msg}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * The badge on a report's session line — one component so the org report and the public shared
 * report cannot drift in wording. Renders nothing for English or an absent field
 * (`languageBadge` explains why that is the only honest rendering).
 */
export function LanguagePill({ language }: { language: string | null | undefined }): ReactNode {
  const badge = languageBadge(language);
  if (!badge) return null;
  return (
    <div style={{ marginTop: 6 }}>
      <span className="pill t" title={badge.title}>
        {badge.label}
      </span>
    </div>
  );
}
